/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { AttributeValue, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';
import MsDyn365, { ICoreContext ,IGridSettings, IImageData, IImageSettings,Image } from '@msdyn365-commerce/core';
import { addThrottledEvent } from '@msdyn365-commerce-modules/utilities';
import { getProductPageUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import { IMfrmProductsCompareData } from './mfrm-products-compare.data';
import { IMfrmProductsCompareProps } from './mfrm-products-compare.props.autogenerated';
import { ProductCompareCarouselItem } from './components/product-compare-carousel-item';
import { variantQueryStringName } from '../../Utilities/constants';
import { renderProductBadge } from '../../Utilities/get-product-badge';
import { parseBrandNameUnbxd } from '../../Utilities/get-product-brand';
import _getViewPort from '../../Utilities/get-view-port';
import GetBatchDeliveryMessage, { IBatchDeliveryMessage } from '../../Utilities/get-delivery-message-batch';
import { getAttributeArrayValueByNameUnbxd, getAttributeValueByNameUnbxd, getunbxdAttributeNameByCRTName } from '../../Utilities/getAttributeUtils';
import Cookies from 'universal-cookie';
import { _getProductType } from '../../themes/mattressfirm/views/components/delivery-message/deliverymessage.component';
import { _getSoonestDeliveryDate } from '../../Utilities/product-search-result-utils';
import { IFetchedCategoryIds } from '../mfrm-product-recommendations/mfrm-product-recommendations';
import { useInView } from 'react-intersection-observer';
import getUnbxdCollectionCompare, { GetUnbxdCollectionCompareDataInput, IGetUnbxdCollectionCompareDataData } from './get-unbxd-collection-compare-data.action';
import { IMFICartLine } from '../../actions/BatchCoreProductDataServiceEntities.g';
import getListOfCoreDeliveryMessage, { IMFIATPInventoryEntityPLPEx } from '../../Utilities/get-core-delivery-batch';
import { getZipCode } from '../../Utilities/get-zipcode';
import { BVRatingSummary } from '../../Utilities/components/BVRatingSummary';
import { getBVAttributeValuesUnbxd } from '../../Utilities/get-bv-attributes';
import { renderRating } from '../../Utilities/get-rating';
import { removeSpecialChars } from '../../Utilities/allRegex';
import { urlFallBackImage } from '../../Utilities/fallbackImageUrl';
import { generateImageUrl } from '@msdyn365-commerce-modules/retail-actions';


export interface IMfrmProductsCompareViewProps extends IMfrmProductsCompareProps<IMfrmProductsCompareData> {
    carouselItems: React.ReactNode[] | null;
    mattributeHeadings: string[],
    compareProductsList: SimpleProductAndAttributes[],
    isDesktop: boolean,
    collectionDescription: string | undefined
    compareRef?: (node?: Element | null | undefined) => void
}
export interface IMfrmAttributesList {
    RecordId: string | undefined;
    status: string | undefined;
    value: AttributeValue[] | undefined;
}
export interface SimpleProductAndAttributes extends SimpleProduct {
    Attributes?: AttributeValue[] | undefined;
    Category?: IFetchedCategoryIds | undefined;
}

/**
 *
 * MfrmProductsCompare component
 * @extends {React.PureComponent<IMfrmProductsCompareProps<IMfrmProductsCompareData>>}
 */
export const MfrmProductsCompare: React.FC<IMfrmProductsCompareProps<IMfrmProductsCompareData>> = props => {

    let resizeThrottledEventHandler: (event: Event) => void;
    const [compareData, setCompareData] = React.useState<IGetUnbxdCollectionCompareDataData | undefined>(undefined);
    const [isDesktop, setIsDesktop] = React.useState(false);
    const [deliveryMessages, setDeliveryMessages] = React.useState<IBatchDeliveryMessage[]>([]);
    const [coreDeliveryMessages, setCoreDeliveryMessages] = React.useState<IMFIATPInventoryEntityPLPEx[]>([]);
    const onInitial = React.useRef(true);
    const unbxdData = props.data.unbxdCollectionCompare?.result;
    let collectionDescription = unbxdData?.collectionDescription;
    const cookies = new Cookies();
    const zipcode = cookies.get('zip_code');
    const attrListName = props.context.app.config?.attributeNamesMapingCrtToUnbxd;
    const updateViewPort = (context: ICoreContext): void => {
        const viewport = _getViewPort(context);
        if (viewport === 'lg' || viewport === 'xl') {
            setIsDesktop(true);
        } else {
            setIsDesktop(false);
        }
    };
    const defaultImageSettings: IImageSettings = {
      viewports: {
        xs: { q: `w=0&h=210&m=6`, w: 0, h: 0 },
        lg: { q: `w=0&h=210&m=6`, w: 0, h: 0 },
        xl: { q: `w=0&h=210&m=6`, w: 0, h: 0 }
      },
      lazyload: true
    };

    const getVariantsList = (): any[] => {
        const variantsData: any[] = [];
        const currentProductId = unbxdData?.productId;
        if (currentProductId && currentProductId === props?.data?.product?.result?.RecordId) {
            unbxdData?.collectionData?.products.map((product: any) => {
                product?.variants?.map((variant: any, index: number) => {
                    variantsData.push(variant);
                });
            });
            collectionDescription = unbxdData?.collectionDescription;
        }
        else if (compareData?.collectionData?.products) {
            compareData?.collectionData?.products.map((product: any) => {
                product?.variants?.map((variant: any, index: number) => {
                    variantsData.push(variant);
                });
            });
            collectionDescription = compareData?.collectionDescription;
        }
        return variantsData;
    };
    const compareProductsList = getVariantsList();

    const callTealiumOnLoad = async () => {
        if (compareProductsList.length) {
            // @ts-ignore
            if ('utag' in window) {
                const positions: string[] = [];
                const productPIds: string[] = [];
                const productsName: string[] = [];
                const productsCategory: string[] = [];
                const productsBrand: string[] = [];
                const productsVariant: string[] = [];
                const productsPrice: string[] = [];
                compareProductsList.slice(1).forEach((item: any, index: number) => {
                    const retailVariantid = getAttributeValueByNameUnbxd(item, 'v_VariantId');
                    const count = index + 1;
                    positions.push(`${count}`);
                    item?.ItemId && productPIds.push(item?.ItemId);
                    item?.Name && productsName.push(item?.Name);
                    item?.categoryPathId2.length && productsCategory.push(getAttributeArrayValueByNameUnbxd(item, 'categoryPathId2')?.at(-2)?.split('|')[1] ?? '');
                    productsBrand.push(parseBrandNameUnbxd(item, getunbxdAttributeNameByCRTName(props?.context?.app?.config?.brandBackofficeAttributePdp, attrListName) ?? '') || '');
                    retailVariantid && productsVariant.push(retailVariantid?.toString());
                    productsPrice.push(item?.v_Price?.toString());
                });
                // @ts-ignore
                // tslint:disable-next-line: object-literal-key-quotes
                // eslint-disable-next-line security/detect-non-literal-fs-filename
                utag.link({
                    event: 'product-impression',
                    enhanced_action: 'product_impression',
                    event_category: 'ecommerce',
                    event_action: 'product impression',
                    event_label: getPagePath() ?? '',
                    event_noninteraction: 'true',
                    product_impression_list_name: [`PDP Collection Compare`],
                    product_impression_position: positions,
                    product_impression_id: productPIds,
                    product_impression_name: productsName,
                    product_impression_category: productsCategory,
                    product_impression_brand: productsBrand,
                    product_impression_variant: productsVariant,
                    product_impression_price: productsPrice
                });
            }
        }
    };

    const getPagePath = (): string => {
        return MsDyn365.isBrowser
            ? window.location.href.split(location.host)[1].includes('?')
                ? window.location.href.split(location.host)[1].split('?')[0]
                : window.location.href.split(location.host)[1]
            : '';
    };

    const getMattributeData = (attributes: any | undefined, uniqueHeadings: string[]): any => {
        const mattributeData: any = [];
        uniqueHeadings &&
            uniqueHeadings.length > 0 &&
            uniqueHeadings.map((item, index) => {
                const scoreAttribute = attributes && attributes[getunbxdAttributeNameByCRTName(`${item} score`?.toLowerCase(), attrListName) ?? ''];
                const mattributeValue = attributes && attributes[getunbxdAttributeNameByCRTName(`${item}`?.toLowerCase(), attrListName) ?? ''];
                const obj = {
                    mattributeName: item,
                    score: scoreAttribute,
                    mattributeValue
                };
                mattributeData.push(obj);
            });
        return mattributeData;
    };

    const getUniqueMattributeData = (): string[] => {
        const mattributeHeadings: string[] = [];
        compareProductsList?.map((product: any) => {
            const spotlightAttributeKey = product && product[getunbxdAttributeNameByCRTName('PDP_Feature Spotlight_Sequence', attrListName) ?? ''];
            const spotlightValues = spotlightAttributeKey ?? undefined;
            if (spotlightValues !== undefined) {
                const spotlightValuesArr = spotlightValues && spotlightValues.split(',');
                spotlightValuesArr &&
                    spotlightValuesArr.length > 0 &&
                    spotlightValuesArr.map((item: any, index: number) => {
                        const attribute = product && product[getunbxdAttributeNameByCRTName(item, attrListName) ?? ''];
                        attribute && !!attribute && mattributeHeadings.push(item);
                    });
            }
        });
        return [...new Set(mattributeHeadings)]?.sort((a, b) => a.toUpperCase() > b.toUpperCase() ? 1 : -1)?.sort((a: any, b: any) => {
            if (props.config.booleanMattributes?.find(e => e?.mattributeName?.toUpperCase() === a?.toUpperCase())) {
                return 1;
            } else if (props.config.booleanMattributes?.find(e => e?.mattributeName?.toUpperCase() === b?.toUpperCase())) {
                return -1;
            } else {
                return 0;
            }
        });
    };

    const getRatings = (attributes: any | undefined): React.ReactNode | undefined => {
        return attributes && renderRating(
            props.context,
            props.typeName,
            props.id,
            getBVAttributeValuesUnbxd(attributes, getunbxdAttributeNameByCRTName('BVAverageOverallRating', attrListName) ?? ''),
            getBVAttributeValuesUnbxd(attributes, getunbxdAttributeNameByCRTName('BVTotalReviewCount', attrListName) ?? ''),
            props.resources.ratingAriaLabel
        );
    };

    const _renderDeliveryMessage = (recordId: number | undefined, shippingInfo: string | undefined) => {
        if (!deliveryMessages || deliveryMessages.length < 1) {
            return <></>;
        }
        let deliveryMessage: string | undefined = '';
        if (_getProductType(shippingInfo).toLowerCase() === 'core' && coreDeliveryMessages && coreDeliveryMessages.find((item: any) => Number(item.VariantRecordId) === recordId)) {
            const atpDeliveryMsg = coreDeliveryMessages.find((item: any) => Number(item.VariantRecordId) === recordId)?.deliveryMessage;
            if (atpDeliveryMsg && atpDeliveryMsg !== '') {
                deliveryMessage = atpDeliveryMsg;

            }
        }
        if (!deliveryMessage || deliveryMessage === '') {
            deliveryMessage = deliveryMessages?.find(msg => msg?.VariantId === recordId)?.DeliveryMessage;
        }

        return (
            <div className='product-compare-card__delivery'>
                <span className='product-compare-card__delivery-label'>{props.resources.deliveryBy}</span>
                <div className='product-compare-card__delivery-message'>
                    {deliveryMessage}
                    {zipcode && <><span> to</span><span className='product-compare-card__delivery-zipcode'>{zipcode}</span></>}
                </div>
            </div>
        );
    };

    const [compareRef, inView] = useInView({
        threshold: 0,
        triggerOnce: true
    });
    function renderImage(
      imageSettings?: IImageSettings,
      gridSettings?: IGridSettings,
      imageUrl?: string,
      fallbackImageUrl?: string,
      altText?: string
  ): JSX.Element | null {
      if (!fallbackImageUrl || !gridSettings || !imageSettings) {
          return null;
      }
      const img: IImageData = {
          src: imageUrl ? imageUrl : fallbackImageUrl,
          altText: altText ? altText : '',
          fallBackSrc: fallbackImageUrl
      };
      const imageProps = {
          gridSettings,
          imageSettings,
          fallbackImageUrl
      };
      imageProps.imageSettings.cropFocalRegion = true;
      return <Image {...img} {...imageProps} imageFallbackOptimize={true} loadFailureBehavior='empty' />;
  }

    const _renderCaouselItems = (): React.ReactNode[] | null => {
        const uniqueHeadings = getUniqueMattributeData();
        const sortedArray = compareData?.variantIds ?? unbxdData?.variantIds;
        return sortedArray && compareProductsList?.sort((a: any, b: any) => sortedArray?.indexOf(a?.variantId) - sortedArray?.indexOf(b?.variantId))?.map((product: any, index: number) => {
            const queryBasedSelection = props?.context?.app?.config?.queryBasedVariantSelection;
            let productUrl = getProductPageUrlSync(removeSpecialChars(product?.Name!, props.context) || '', queryBasedSelection ? product.MasterProductId || product.variantId : product.variantId, props.context && props.context.actionContext, undefined);
            if (queryBasedSelection && product.MasterProductId) {
                const variantIdQueryParams = productUrl.includes('?') ? `&${variantQueryStringName}=${product.variantId}` : `?${variantQueryStringName}=${product.variantId}`;
                productUrl += variantIdQueryParams;
            }
            const productMattributeData = getMattributeData(product, uniqueHeadings);
            const comfort = getAttributeValueByNameUnbxd(product, getunbxdAttributeNameByCRTName('Comfort', attrListName)) ?? "";
            const sleepPosition = getAttributeValueByNameUnbxd(product, getunbxdAttributeNameByCRTName('Sleep Position', attrListName)) ?? "";
            const bestFor = props.config.sleepStyleOptions?.find(option => option.selectedSleepStyle === sleepPosition);
            const mattressType = getAttributeValueByNameUnbxd(product, getunbxdAttributeNameByCRTName('Product Type', attrListName)) ?? "";
            const heigth = getAttributeValueByNameUnbxd(product, getunbxdAttributeNameByCRTName('Height', attrListName)) ?? "";
            const brandName = parseBrandNameUnbxd(product, getunbxdAttributeNameByCRTName(props?.context?.app?.config?.brandBackofficeAttributePdp, attrListName) ?? '') || '';
            const title = brandName ? `${brandName} ${product?.Name}` : product?.Name ?? '';
            const comfortIcons = props.data.comfortTypesData.result;
            const badgeValue = getAttributeValueByNameUnbxd(product, getunbxdAttributeNameByCRTName(props?.context?.app?.config?.productBadgeAttribute, attrListName));
            const deliveryByMessage = _renderDeliveryMessage(Number(product?.variantId), getAttributeValueByNameUnbxd(product, getunbxdAttributeNameByCRTName(props?.context?.app?.config?.smallParcelAttributeKey, attrListName)) ?? '');
            const emptyPlaceHolderImage = props.context.app.config?.placeholderImageName as string;
            const imageUrl = generateImageUrl(product?.PrimaryImageUrl, props.context.request.apiSettings);
            const fallBackImageUrl = urlFallBackImage(emptyPlaceHolderImage, product?.ItemId, props.context.actionContext.requestContext);
            return (
                <ProductCompareCarouselItem
                    index={index}
                    key={index}
                    productUrl={productUrl}
                    title={title}
                    image={renderImage(
                        defaultImageSettings,
                        props.context.request.gridSettings,
                        imageUrl,
                        fallBackImageUrl,
                        product?.Name)}
                    price={product?.v_Price}
                    basePrice={product?.v_AdjustedPrice}
                    ratings={props.config.showBVRatings ? <BVRatingSummary itemId={product?.ItemId} variantId={props?.data?.product?.result?.RecordId} /> : getRatings(product)}
                    mattributeData={productMattributeData}
                    productBadge={renderProductBadge(badgeValue, props?.context?.request)}
                    deliveryMessage={deliveryByMessage}
                    comfort={comfort}
                    mattressType={mattressType}
                    heigth={heigth}
                    bestFor={bestFor?.sleepStyleDisplayName}
                    product={product}
                    brandName={brandName}
                    context={props.context}
                    comfortIcon={comfortIcons && comfortIcons[comfort] && comfortIcons[comfort]?.icon}
                    booleanMattributes={props.config.booleanMattributes}
                />
            );
        });
    };

    const getDeliveryMessages = async (productIdsAndtype: IBatchDeliveryMessage[]) => {
        const data = GetBatchDeliveryMessage(productIdsAndtype);
        void getCoreProductsDelivery(productIdsAndtype);
        if (data) {
            setDeliveryMessages(data);
        }
    };
    const getCoreProductsDelivery = async (productIdsAndtype: IBatchDeliveryMessage[]) => {
        productIdsAndtype = productIdsAndtype.filter(product => _getProductType(product.ShippingInfo).toLowerCase() === 'core');
        const itemLines: IMFICartLine[] = productIdsAndtype?.map(product => {
            return {
                ItemId: product?.ItemId,
                Quantity: 1,
                VariantRecordId: product.VariantId.toString()
            };
        });
        if (itemLines && itemLines.length > 0) {
            const atpResponse = await getListOfCoreDeliveryMessage(props.context.actionContext, itemLines, getZipCode(props.context.actionContext));
            atpResponse !== undefined && setCoreDeliveryMessages(atpResponse);

        }
    };

    const UpdateProductsData = async () => {
        const currentProductId = unbxdData?.productId;
        if (!currentProductId || currentProductId !== props?.data?.product?.result?.RecordId) {
            // get data from dataAction
            const response: IGetUnbxdCollectionCompareDataData = await getUnbxdCollectionCompare(new GetUnbxdCollectionCompareDataInput(props.config, props?.data?.product?.result?.RecordId?.toString()), props.context.actionContext);
            if (response?.collectionData?.products) {
                setCompareData(response);
            } else {
                setCompareData(undefined);
            }
        }
    };

    React.useEffect(() => {
        void UpdateProductsData();
    }, [props?.data?.product?.result?.RecordId]);

    React.useEffect(() => {
        if (compareProductsList && compareProductsList.length && compareProductsList.length >= 1) {
            const productIdsAndtype: IBatchDeliveryMessage[] = compareProductsList.map((product: any) => {
                return {
                    ItemId: product?.ItemId,
                    VariantId: Number(product?.variantId),
                    brandList: props.context.app.config.brandList,
                    coreDefaultMessage: props.context.app.config.defaultDeliveryMessage,
                    deliveryDays: product[getunbxdAttributeNameByCRTName(props.context.app.config.leadTime, attrListName) ?? ''] ?? '',
                    Brand: product[getunbxdAttributeNameByCRTName(props.context.app.config.brandBackofficeAttributePdp, attrListName) ?? ''] ?? '',
                    ShippingInfo: product[getunbxdAttributeNameByCRTName(props.context.app.config.smallParcelAttributeKey, attrListName) ?? ''] ?? '',
                    DeliveryMessage: ""
                };
            });
            void getDeliveryMessages(productIdsAndtype);
        }
    }, [compareData, props?.data?.product?.result?.RecordId, zipcode]);

    React.useEffect(() => {
        if (inView) {
            void callTealiumOnLoad();
        }
    }, [inView]);

    React.useEffect(() => {
        if (MsDyn365.isBrowser) {
            resizeThrottledEventHandler = addThrottledEvent(window, 'resize', () => updateViewPort(props.context));
        }
        return () => {
            MsDyn365.isBrowser && window.removeEventListener('resize', resizeThrottledEventHandler!, false);
        };
    }, [props.context.request.gridSettings, MsDyn365.isBrowser && window.innerWidth]);

    React.useEffect(() => {
        if (onInitial.current) {
            onInitial.current = false;
        } else {
            void callTealiumOnLoad();
        }
    }, [props?.data?.product?.result?.RecordId]);

    if (!compareProductsList?.length) {
        return <></>;
    }
    return props.renderView({
        ...props,
        carouselItems: compareProductsList ? _renderCaouselItems() : null,
        mattributeHeadings: getUniqueMattributeData(),
        compareProductsList: compareProductsList,
        isDesktop: isDesktop,
        collectionDescription,
        compareRef: compareRef
    });
};

export default MfrmProductsCompare;